import { Component, EventEmitter, OnInit } from '@angular/core';
import { ExpenditureService } from '../expenditure.service';
import { Expenditure } from '../expenditure';
import { NotificationService } from '../../shared/notification.service';

@Component({
  selector: 'app-expenditure-list',
  templateUrl: 'list.component.html',
  styleUrls: [],
})
export class ExpenditureListComponent implements OnInit {
  public expenditure: Expenditure;

  /**
   * A list of all the expenditures currently loaded.
   *
   * @type {Expenditure[]}
   */
  public expenditures: Expenditure[] = [];

  /**
   * Are we currently performing a loading action?
   *
   * @type {boolean}
   */
  public isLoading: boolean = true;

  /**
   * The total number of pages in this list of expenditures.
   *
   * @type {number}
   */
  public totalPages: number = 1;

  /**
   * The current page of expenditures that we are viewing.
   *
   * @type {number}
   */
  public currentPage: number = 1;

  /**
   * The total number of expenditures that can be viewed in the system.
   *
   * @type {number}
   */
  public totalResults: number = 0;

  /**
   * An Emitter to alert other components when a expenditure has been deleted.
   *
   * @type {EventEmitter<boolean>}
   */
  public expenditureDeleted: EventEmitter<boolean> = new EventEmitter<
    boolean
  >();

  /**
   * Load up all our required services and subscribe to every observable we need.
   *
   * @param {ExpenditureService} expenditureService
   * @param {NotificationService} notificationService
   */
  constructor(
    private expenditureService: ExpenditureService,
    private notificationService: NotificationService,
  ) {
    this.expenditureService.loading.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.expenditureService.totalPages.subscribe(totalPages => {
      this.totalPages = totalPages;
    });
    this.expenditureService.currentPage.subscribe(currentPage => {
      this.currentPage = currentPage;
    });
    this.expenditureService.totalResults.subscribe(totalResults => {
      this.totalResults = totalResults;
    });
  }

  /**
   * When the component is initialised we set it to load
   * page one of the expenditure list.
   */
  public ngOnInit(): void {
    this.loadExpenditures(1);
  }

  /**
   * When we receive a page change from the pagination
   * component we load the appropriate page for the
   * expenditure list.
   *
   * @param {number} pageNumber
   */
  public onPageChanged(pageNumber: number): void {
    this.loadExpenditures(pageNumber);
  }

  /**
   * Loads a list of expenditures using the given per page limit.
   *
   * @param {number} page The current page we require.
   * @param {number} limit The number of results per page.
   */
  public loadExpenditures(page?: number, limit?: number): void {
    const listExpenditures = this.expenditureService
      .listExpenditures(page, limit)
      .subscribe((expenditures: Expenditure[]) => {
        this.expenditures = expenditures;
        listExpenditures.unsubscribe();
      });
  }

  /**
   * When receiving confirmation of an item being deleted
   * we call the back end with the appropriate expenditure ID
   * to delete the expenditure.
   *
   * @param {string} id
   */
  public delete(id: string) {
    this.expenditureService.deleteExpenditure(id).subscribe(
      deleted => {
        this.notificationService.success(
          `Expenditure Deleted`,
          `Expenditure with ID ${id} was deleted.`,
          5,
        );
        this.expenditureDeleted.next(true);
        this.onPageChanged(this.currentPage);
      },
      err => {
        this.notificationService.error(
          `Expenditure Not Deleted`,
          err.error.message,
          10,
        );
        this.expenditureDeleted.next(false);
        this.onPageChanged(this.currentPage);
      },
    );
  }
}
